import React, { useEffect, useId, useMemo, useState } from "react";
import styled, { css, useTheme } from "styled-components";
import moment from "moment";
import useMeasure from "utils/hooks/useMeasure";
import { hexToRGB } from "@shared/utils/misc";
import {
  directionColor,
  getNewLookTokens,
  newLookType,
} from "common/NewLook/tokens.ts";
import { monotonePath, Point } from "common/NewLook/monotonePath.ts";
import { PnlPoint } from "features/newMarketplace/NewLook/useNetPnlSeries.ts";
import { formatPercent } from "features/newMarketplace/NewLook/format.ts";
import { PNL_CHART_GUIDE_ID, PNL_CHART_ID } from "testIds";
import { formatDateLabel } from "@shared/utils/date";

interface PnlChartProps {
  points: PnlPoint[];
  /** The period's net result; the line takes its direction colour. */
  change: number;
  ariaLabel: string;
  /** Fixed height; omit to fill the parent's height. */
  height?: number;
  /** Value guides on the right and dates under the plot. */
  axes?: boolean;
  /** The point under the crosshair (undefined when it leaves). */
  onActiveChange?: (point?: PnlPoint) => void;
  /** Hide the resting end dot (edge-to-edge charts would clip it). */
  hideEndDot?: boolean;
  /**
   * The line runs the full width (the panel bleeds it to its edges); value
   * labels sit inside the plot above their guides, dates keep a gutter.
   */
  edgeToEdge?: boolean;
}

const EDGE_GUTTER = 24;

const AXIS_WIDTH = 48;
const TOP = 10;
const AXIS_BOTTOM = 22;
const GUIDE_COUNT = 3;

const niceStep = (span: number) => {
  const raw = span / GUIDE_COUNT || 1;
  const exponent = Math.floor(Math.log10(raw));
  const fraction = raw / Math.pow(10, exponent);
  const nice =
    fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 2.5 ? 2.5 : 5;
  return {
    step: nice * Math.pow(10, exponent),
    digits: Math.max(0, (nice === 2.5 ? 1 : 0) - exponent),
  };
};

const getDomain = (values: number[], axes?: boolean) => {
  let min = Math.min(...values, ...(axes ? [0] : []));
  let max = Math.max(...values, ...(axes ? [0] : []));
  if (max === min) {
    min -= 1;
    max += 1;
  }
  const span = max - min;
  return { min: min - span * (axes ? 0.08 : 0.2), max: max + span * 0.12 };
};

const getGuides = (min: number, max: number) => {
  const { step, digits } = niceStep(max - min);
  const guides: number[] = [];
  for (let value = Math.ceil(min / step) * step; value <= max; value += step) {
    guides.push(Math.abs(value) < step / 1000 ? 0 : value);
  }
  return { guides, digits };
};

/**
 * A channel's net PnL over time: a smooth line in the direction of the
 * period's result with a soft fill under it. Compact (cards) it fills its box
 * down to the edge; with axes (the group page) it adds value guides, dates
 * and a time chip under the crosshair. The headline next to the chart reads
 * out the hovered point, so nothing floats over the line.
 */
const PnlChart = ({
  points,
  change,
  ariaLabel,
  height: fixedHeight,
  axes,
  edgeToEdge,
  onActiveChange,
  hideEndDot,
}: PnlChartProps) => {
  const theme = useTheme();
  const tokens = getNewLookTokens(theme);
  const gradientId = `pnl-${useId().replace(/[^\w-]/g, "")}`;
  const [measureRef, size] = useMeasure<HTMLDivElement>();
  const [active, setActive] = useState<number>();
  const width = size?.width || 0;
  const height = fixedHeight ?? (size?.height || 0);
  const color = change ? directionColor(tokens, change) : tokens.accent;

  const plotWidth = Math.max(0, width - (axes && !edgeToEdge ? AXIS_WIDTH : 0));
  const plotBottom = height - (axes ? AXIS_BOTTOM : 0);

  const shape = useMemo(() => {
    if (points.length < 2 || !plotWidth || plotBottom <= TOP) return undefined;
    const { min, max } = getDomain(
      points.map((point) => point.value),
      axes
    );
    const t0 = points[0].time;
    const t1 = points[points.length - 1].time;
    const x = (time: number) =>
      t1 === t0 ? 0 : ((time - t0) / (t1 - t0)) * plotWidth;
    const y = (value: number) =>
      TOP + (1 - (value - min) / (max - min)) * (plotBottom - TOP);
    const coords: Point[] = points.map((point) => [
      x(point.time),
      y(point.value),
    ]);
    const line = monotonePath(coords);
    const last = coords[coords.length - 1];
    const area = `${line}L${last[0]},${plotBottom}L${coords[0][0]},${plotBottom}Z`;
    const { guides, digits } = getGuides(min, max);
    const ticks = [0, 1 / 3, 2 / 3, 1].map((share) => ({
      share,
      x: share * plotWidth,
      time: t0 + (t1 - t0) * share,
    }));
    return {
      coords,
      line,
      area,
      ticks,
      digits,
      guides: guides.map((value) => ({ value, y: y(value) })),
      zeroY: min < 0 && max > 0 ? y(0) : undefined,
    };
  }, [points, plotWidth, plotBottom, axes]);

  const activePoint = active !== undefined ? points[active] : undefined;
  useEffect(() => {
    onActiveChange?.(activePoint);
  }, [activePoint, onActiveChange]);
  useEffect(() => setActive(undefined), [points]);

  const onPointer = (clientX: number, element: HTMLElement) => {
    if (!shape) return;
    const pointerX = clientX - element.getBoundingClientRect().left;
    let nearest = 0;
    shape.coords.forEach(([coordX], index) => {
      if (
        Math.abs(coordX - pointerX) <
        Math.abs(shape.coords[nearest][0] - pointerX)
      )
        nearest = index;
    });
    setActive(nearest);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (!shape || (event.key !== "ArrowLeft" && event.key !== "ArrowRight"))
      return;
    event.preventDefault();
    const step = event.key === "ArrowLeft" ? -1 : 1;
    setActive((current) =>
      Math.min(
        shape.coords.length - 1,
        Math.max(0, (current ?? shape.coords.length - 1) + step)
      )
    );
  };

  const activeCoord =
    active !== undefined && shape ? shape.coords[active] : undefined;
  const lastCoord = shape?.coords[shape.coords.length - 1];
  const axisText = (key: string | number, props: object, text: string) => (
    <AxisText key={key} fill={tokens.muted} {...props}>
      {text}
    </AxisText>
  );

  return (
    <Wrapper
      ref={measureRef}
      style={fixedHeight ? { height: fixedHeight } : undefined}
      $fill={!fixedHeight}
      role="img"
      aria-label={ariaLabel}
      data-testid={PNL_CHART_ID}
      tabIndex={axes && shape ? 0 : -1}
      onMouseMove={(event) => onPointer(event.clientX, event.currentTarget)}
      onMouseLeave={() => setActive(undefined)}
      onBlur={() => setActive(undefined)}
      onKeyDown={axes ? onKeyDown : undefined}
    >
      {shape && (
        <svg width={width} height={height} aria-hidden={true}>
          <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
              <stop
                offset="0%"
                stopColor={color}
                stopOpacity={theme.isDarkMode ? 0.22 : 0.14}
              />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          {axes &&
            shape.guides.map((guide) => (
              <g key={guide.value} data-testid={PNL_CHART_GUIDE_ID}>
                <line
                  x1={0}
                  x2={plotWidth}
                  y1={guide.y}
                  y2={guide.y}
                  stroke={tokens.hairlineStrong}
                  strokeDasharray={guide.value === 0 ? undefined : "2 4"}
                />
                {axisText(
                  guide.value,
                  edgeToEdge
                    ? {
                        x: plotWidth - EDGE_GUTTER,
                        y: guide.y - 5,
                        textAnchor: "end",
                      }
                    : { x: plotWidth + 8, y: guide.y + 4 },
                  formatPercent(guide.value, guide.value ? shape.digits : 0)
                )}
              </g>
            ))}
          {!axes && shape.zeroY !== undefined && (
            <line
              x1={0}
              x2={plotWidth}
              y1={shape.zeroY}
              y2={shape.zeroY}
              stroke={tokens.hairlineStrong}
              strokeDasharray="2 4"
            />
          )}
          {axes &&
            shape.ticks.map((tick) =>
              axisText(
                tick.share,
                {
                  x: edgeToEdge
                    ? Math.min(
                        plotWidth - EDGE_GUTTER,
                        Math.max(EDGE_GUTTER, tick.x)
                      )
                    : tick.x,
                  y: height - 6,
                  textAnchor:
                    tick.share === 0
                      ? "start"
                      : tick.share === 1
                      ? "end"
                      : "middle",
                },
                formatDateLabel(moment.unix(tick.time), "MMM D")
              )
            )}
          <path d={shape.area} fill={`url(#${gradientId})`} />
          <path
            d={shape.line}
            fill="none"
            stroke={color}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {lastCoord && !activeCoord && !hideEndDot && (
            <circle
              cx={lastCoord[0]}
              cy={lastCoord[1]}
              r={3.5}
              fill={color}
              stroke={tokens.panel}
              strokeWidth={2}
            />
          )}
          {activeCoord && (
            <g>
              <line
                x1={activeCoord[0]}
                x2={activeCoord[0]}
                y1={TOP}
                y2={plotBottom}
                stroke={hexToRGB(tokens.ink, theme.isDarkMode ? 0.35 : 0.25)}
                strokeWidth={1}
              />
              <circle
                cx={activeCoord[0]}
                cy={activeCoord[1]}
                r={4.5}
                fill={color}
                stroke={tokens.panel}
                strokeWidth={2}
              />
            </g>
          )}
        </svg>
      )}
      {axes && activePoint && activeCoord && (
        <TimeChip
          style={{
            left: Math.min(Math.max(activeCoord[0], 44), plotWidth - 44),
            top: height - AXIS_BOTTOM + 3,
          }}
        >
          {formatDateLabel(moment.unix(activePoint.time), "MMM D, YYYY")}
        </TimeChip>
      )}
    </Wrapper>
  );
};

export default React.memo(PnlChart);

const Wrapper = styled.div<{ $fill: boolean }>`
  position: relative;
  width: 100%;
  min-width: 0;
  outline: none;
  cursor: crosshair;
  ${(p) =>
    p.$fill &&
    css`
      height: 100%;
    `}
  svg {
    display: block;
    overflow: visible;
  }
  &:focus-visible {
    box-shadow: 0 0 0 2px ${(p) => getNewLookTokens(p.theme).focus};
    border-radius: 6px;
  }
`;
const AxisText = styled.text`
  font-size: 11px;
  font-variant-numeric: tabular-nums;
`;
const TimeChip = styled.div`
  position: absolute;
  transform: translateX(-50%);
  padding: 1px 8px;
  border-radius: 6px;
  background: ${(p) => getNewLookTokens(p.theme).ink};
  color: ${(p) => getNewLookTokens(p.theme).panel};
  ${newLookType.meta};
  font-size: 11px;
  line-height: 16px;
  font-weight: 500;
  white-space: nowrap;
  pointer-events: none;
`;
